import { Fragment } from "react";
import { connectorDirectory } from "../lib/connectorDirectory";
import {
  SearchableDropdown,
  Divider,
  RowButton,
  SectionLabel,
  SubtleButton,
} from "./SearchableDropdown";
import { type AppInfo, ConnectorOption, ConnectorRow } from "./ConnectorRow";
import { EmptyState } from "./EmptyState";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { useMakeUrl, routes } from "../lib/urls";

export type ConnectedItem = AppInfo & {
  manageTo: string;
  onRemove: () => void;
};

export type AvailableItem = AppInfo & {
  onSelect: () => void;
};

export type DisconnectedItem = {
  id: string;
  appType: string;
  slug?: string;
  options: AppInfo[];
  onSelect: (connectedAppId: string) => void;
  onCreateNew: () => void;
};

type Props = {
  opener: React.ReactNode;
  parentNoun: string;
  connected: ConnectedItem[];
  available: AvailableItem[];
  // Heading for the available-connectors section, e.g. "In this space" when the
  // list is scoped to what the current space can use (defaults tenant-wide).
  availableLabel?: string;
  disconnected?: DisconnectedItem[];
  onCreateApp?: (identifier: string) => void;
  margin?: number;
  side?: React.ComponentProps<typeof SearchableDropdown>["side"];
  style?: React.CSSProperties;
  disabled?: boolean;
  "aria-label"?: string;
};

export function ConnectorSelectorDropdown({
  opener,
  parentNoun,
  connected,
  available,
  availableLabel = "In this tenant",
  disconnected = [],
  onCreateApp,
  margin,
  side,
  style,
  disabled,
  "aria-label": ariaLabel,
}: Props) {
  const makeUrl = useMakeUrl();
  return (
    <SearchableDropdown
      opener={opener}
      margin={margin}
      side={side}
      style={style}
      disabled={disabled}
      aria-label={ariaLabel}
      searchPlaceholder="Search connectors…"
      height={360}
    >
      {(query, close, shiftHeld) => {
        const lowerQuery = query.toLowerCase().trim();
        const match = (name: string) => name.toLowerCase().includes(lowerQuery);
        const filteredConnected = lowerQuery
          ? connected.filter((a) => match(a.name))
          : connected;
        const filteredAvailable = lowerQuery
          ? available.filter((a) => match(a.name))
          : available;
        const customApp = connectorDirectory.find(
          (a) => a.identifier === "apiKey"
        );
        const directoryResults = lowerQuery
          ? connectorDirectory.filter((a) => match(a.name))
          : [];
        const createItems = !onCreateApp
          ? []
          : directoryResults.length
            ? directoryResults
            : filteredConnected.length === 0 &&
                filteredAvailable.length === 0 &&
                customApp
              ? [customApp]
              : [];

        const sections: { label: string; rows: React.ReactNode }[] = [];

        if (filteredConnected.length) {
          sections.push({
            label: `On this ${parentNoun}`,
            rows: filteredConnected.map((app) => (
              <ConnectorOption
                key={app.id}
                app={app}
                to={app.manageTo}
                close={close}
                hover={
                  <Tooltip content={`Remove from ${parentNoun}`}>
                    <SubtleButton
                      icon="circle-minus"
                      variant="secondary"
                      onClick={app.onRemove}
                    />
                  </Tooltip>
                }
              />
            )),
          });
        }

        if (disconnected.length) {
          sections.push({
            label: "Needs connection",
            rows: disconnected.map((item) => {
              const appDef = connectorDirectory.find(
                (a) => a.identifier === item.appType
              );
              const name = item.slug ?? appDef?.name ?? item.appType;
              return (
                <RowButton
                  key={item.id}
                  onClick={() => {
                    item.onCreateNew();
                    close();
                  }}
                >
                  <ConnectorRow
                    template={{ name, identifier: item.appType }}
                    name={name}
                    secondary="Disconnected"
                    grayscale
                  />
                </RowButton>
              );
            }),
          });
        }

        if (filteredAvailable.length) {
          sections.push({
            label: availableLabel,
            rows: filteredAvailable.map((app) => (
              <ConnectorOption
                key={app.id}
                app={app}
                onClick={app.onSelect}
                openTo={makeUrl(routes.modal.connector, { id: app.id })}
                shiftHeld={shiftHeld}
                close={close}
              />
            )),
          });
        }

        if (createItems.length) {
          sections.push({
            label: "Add new",
            rows: createItems.map((app) => (
              <RowButton
                key={app.identifier}
                onClick={() => {
                  onCreateApp?.(app.identifier);
                  close();
                }}
              >
                <ConnectorRow
                  template={{ name: app.name, identifier: app.identifier }}
                  name={app.name}
                  secondary={app.tagLine}
                />
              </RowButton>
            )),
          });
        }

        if (sections.length === 0) {
          return (
            <EmptyState
              iconName={lowerQuery ? "text-search" : "api-key-custom"}
              iconSize={24}
              title={
                lowerQuery ? "No connectors found" : "No connectors available"
              }
            />
          );
        }

        return sections.map((section, i) => (
          <Fragment key={section.label}>
            {i > 0 && <Divider />}
            <SectionLabel>
              <Text variant="text-small" color="var(--text-secondary)">
                {section.label}
              </Text>
            </SectionLabel>
            {section.rows}
          </Fragment>
        ));
      }}
    </SearchableDropdown>
  );
}
